import { Options, RRule, rrulestr } from 'rrule';
import lodash from 'lodash';
import DateF from './date-f';
import repeatModelFrom from './repeat-intervals';

export const DONT_REPEAT = 'dontRepeat';
export const DAILY = 'daily';
export const EVERY_WEEK = 'everyWeek';
export const EVERY_TWO_WEEKS = 'everyTwoWeeks';
export const EVERY_WEEKDAY = 'everyWeekday';
export const EVERY_MONTH_ON_WEEKDAY = 'everyMonthOnWeekday';
export const EVERY_MONTH_ON_DAY = 'everyMonthOnDay';

export type RepeatInterval =
  | typeof DONT_REPEAT
  | typeof DAILY
  | typeof EVERY_WEEK
  | typeof EVERY_TWO_WEEKS
  | typeof EVERY_WEEKDAY
  | typeof EVERY_MONTH_ON_WEEKDAY
  | typeof EVERY_MONTH_ON_DAY;

type ValidRRuleIntervals = Exclude<RepeatInterval, typeof DONT_REPEAT>;

class RRuleFacade {
  date: DateF;
  protected intervals: Record<ValidRRuleIntervals, string>;

  constructor(date: DateF) {
    this.date = date;

    const repeat = repeatModelFrom(date.toUTCString());
    this.intervals = {
      [DAILY]: repeat.daily(),
      [EVERY_WEEK]: repeat.weekly(),
      [EVERY_TWO_WEEKS]: repeat.biWeekly(),
      [EVERY_WEEKDAY]: repeat.everyWeekday(),
      [EVERY_MONTH_ON_WEEKDAY]: repeat.everyNthWeekdayOfMonth(),
      [EVERY_MONTH_ON_DAY]: repeat.everySpecificDayOfMonth(),
    };
  }

  getRRuleFromInterval(interval: RepeatInterval): string | null {
    const rrule = this.intervals[interval];
    if (!rrule) {
      return null;
    }
    return rrule;
  }

  static getIntervalFromRRule(rrule?: string | null): RepeatInterval {
    const isDaily = ({ freq }: Partial<Options>): boolean => freq === RRule.DAILY;
    const isWeekly = ({ freq, interval, byweekday }: Partial<Options>): boolean => (
      freq === RRule.WEEKLY
        && interval === 1
        && Array.isArray(byweekday) && byweekday.length === 1
    );
    const isBiWeekly = ({ freq, interval, byweekday }: Partial<Options>): boolean => (
      freq === RRule.WEEKLY
        && interval === 2
        && Array.isArray(byweekday) && byweekday.length === 1
    );
    const everyWeekDay = ({ freq, interval, byweekday }: Partial<Options>): boolean => {
      const weekdays = [
        RRule.MO.weekday,
        RRule.TU.weekday,
        RRule.WE.weekday,
        RRule.TH.weekday,
        RRule.FR.weekday,
      ];
      return freq === RRule.WEEKLY
        && interval === 1
        && lodash.isEqual(byweekday, weekdays);
    };
    const everyNthWeekdayOfMonth = ({ freq, byweekday, bysetpos }: Partial<Options>): boolean => (
      freq === RRule.MONTHLY
        && !!byweekday
        && !!bysetpos
    );
    const everySpecificDayOfMonth = ({ freq, bymonthday }: Partial<Options>): boolean => (
      freq === RRule.MONTHLY
        && !!bymonthday
    );

    if (!rrule) {
      return DONT_REPEAT;
    }
    const rruleOptions = rrulestr(rrule).options;
    if (isDaily(rruleOptions)) {
      return DAILY;
    }
    if (isWeekly(rruleOptions)) {
      return EVERY_WEEK;
    }
    if (isBiWeekly(rruleOptions)) {
      return EVERY_TWO_WEEKS;
    }
    if (everyWeekDay(rruleOptions)) {
      return EVERY_WEEKDAY;
    }
    if (everyNthWeekdayOfMonth(rruleOptions)) {
      return EVERY_MONTH_ON_WEEKDAY;
    }
    if (everySpecificDayOfMonth(rruleOptions)) {
      return EVERY_MONTH_ON_DAY;
    }

    return DONT_REPEAT;
  }
}

export default RRuleFacade;
